/**
 * Gift + free shipping progress bar — client side.
 *
 * The theme's own FreeShippingBar class (theme.js) already listens for `cart:updated` and sets
 * --progress. This file does not replace it — theme.js is a compiled bundle and edits to it are
 * lost on update — it registers a second listener that runs afterwards and overwrites the value.
 *
 * Two reasons that matters:
 *
 *   1. The built-in class computes progress as a plain ratio against the shipping threshold. With
 *      a gift milestone the markers are spaced evenly, so the fill has to be piecewise or the bar
 *      and its markers disagree.
 *
 *   2. The built-in class sums final_line_price for shippable lines and stops there. The Liquid
 *      subtracts cart level discounts. That mismatch is pre-existing: on every cart update the bar
 *      jumps from the server rendered value to a higher client one, and during a promo with
 *      stacked discounts the gap is large. This recomputes the same way the Liquid does.
 */

(function () {
  'use strict';

  var UNLOCK_CLASS = 'is-unlocking';
  var REACHED_CLASS = 'is-reached';

  /**
   * Qualifying total, matching snippets/gift-shipping-bar.liquid exactly: shippable lines only,
   * less cart level discounts.
   *
   * @param {object} cart
   * @returns {number} amount in cents
   */
  function qualifyingTotal(cart) {
    var items = (cart && cart.items) || [];

    var total = items.reduce(function (sum, item) {
      return item.requires_shipping ? sum + item.final_line_price : sum;
    }, 0);

    var discounts = (cart && cart.cart_level_discount_applications) || [];

    total = discounts.reduce(function (sum, d) {
      return sum - (d.total_allocated_amount || 0);
    }, total);

    return Math.max(total, 0);
  }

  /**
   * Piecewise when a gift milestone is present, plain ratio when it is not.
   *
   * @param {number} total
   * @param {number} gift - gift threshold in cents, 0 when absent
   * @param {number} shipping - shipping threshold in cents
   * @returns {number} 0..1
   */
  function progressFor(total, gift, shipping) {
    if (!shipping) return 0;

    if (!gift || gift >= shipping) {
      return Math.min(total / shipping, 1);
    }

    if (total >= shipping) return 1;

    if (total >= gift) {
      var span = Math.max(shipping - gift, 1);
      return (1 + (total - gift) / span) / 2;
    }

    return total / gift / 2;
  }

  /**
   * @param {Element} bar
   * @param {number} ratio
   * @param {boolean} allowCelebrate - false on first paint, so a milestone the shopper cleared
   *   before this page loaded does not replay its animation.
   */
  function paint(bar, ratio, allowCelebrate) {
    bar.style.setProperty('--progress', String(ratio));

    var milestones = bar.querySelectorAll('[data-milestone-ratio]');

    for (var i = 0; i < milestones.length; i++) {
      var el = milestones[i];
      var at = parseFloat(el.getAttribute('data-milestone-ratio'));
      var wasReached = el.classList.contains(REACHED_CLASS);
      var isReached = ratio >= at;

      if (isReached && !wasReached) {
        el.classList.add(REACHED_CLASS);

        if (allowCelebrate && !prefersReducedMotion()) {
          unlock(el);
        }
      } else if (!isReached && wasReached) {
        // Removing an item drops the bar back. Re-lock quietly, never in reverse.
        el.classList.remove(REACHED_CLASS, UNLOCK_CLASS);
      }
    }
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /** @param {Element} el */
  function unlock(el) {
    el.classList.remove(UNLOCK_CLASS);
    // Forcing a reflow restarts the animation when the same milestone is crossed twice quickly.
    void el.offsetWidth;
    el.classList.add(UNLOCK_CLASS);

    el.addEventListener(
      'animationend',
      function () { el.classList.remove(UNLOCK_CLASS); },
      { once: true }
    );

    el.dispatchEvent(
      new CustomEvent('cart:milestone-reached', {
        bubbles: true,
        detail: { goal: parseInt(el.getAttribute('data-milestone-goal'), 10) || null }
      })
    );
  }

  /** @param {object} cart */
  function update(cart) {
    var bars = document.querySelectorAll('free-shipping-bar[data-shipping-threshold]');

    if (!bars.length) return;

    var total = qualifyingTotal(cart);

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var gift = parseInt(bar.getAttribute('data-gift-threshold'), 10) || 0;
      var shipping = parseInt(bar.getAttribute('data-shipping-threshold'), 10) || 0;

      paint(bar, progressFor(total, gift, shipping), true);
    }
  }

  // Registered after theme.js has defined its own handler, so this runs second and its value is
  // the one that sticks.
  document.documentElement.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail && event.detail.cart;

    if (cart) update(cart);
  });

  // Mini-cart re-renders through the Section Rendering API, which replaces the bar element. Seed
  // any new bar from what Liquid already wrote, without celebrating.
  function seed() {
    var bars = document.querySelectorAll('free-shipping-bar[data-shipping-threshold]');

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var ratio = parseFloat(bar.style.getPropertyValue('--progress')) || 0;

      paint(bar, ratio, false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', seed);
  } else {
    seed();
  }
})();